/**
 * GroupBlock Component (Group Sub-Timeline feature)
 *
 * Visual representation of a placed group on the timeline.
 *
 * Features:
 * - Displays group name
 * - Purple/violet color (#8b5cf6) to distinguish from body part tools
 * - Width calculated from group duration (non-resizable)
 * - Selection outline on click
 * - Double-click to enter group edit mode
 * - Draggable for repositioning on timeline
 * - Tooltip showing group info (name, tool count, duration)
 * - Puppet badge for multi-puppet support
 * - Hover delete button (X) in top-right corner
 */

import { useCallback, useMemo, useRef, useState, memo } from 'react';
import { useDrag } from 'react-dnd';
import { motion } from 'framer-motion';
import * as Tooltip from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';
import type { PlacedGroup, Group } from '../../stores/timelineStore';
import { getGroupDuration, useTimelineStore } from '../../stores/timelineStore';
import { useUiStore } from '../../stores/uiStore';
import { usePuppetStore } from '../../stores/puppetStore';
import { DND_ITEM_TYPES } from '../sidebar/dndTypes';
import type { PlacedGroupDragItem } from '../sidebar/dndTypes';
import { MIN_VISUAL_WIDTH } from './ToolBlock';
import { formatDurationDisplay } from './utils/formatDuration';
import { useTimelineBlock } from './hooks/useTimelineBlock';

/**
 * Group block color - purple/violet to distinguish from body part tools
 */
const GROUP_COLOR = '#8b5cf6';

/**
 * Hover color - slightly lighter purple
 */
const GROUP_HOVER_COLOR = '#a78bfa';

interface GroupBlockProps {
  /**
   * Placed group instance from timeline store
   */
  placedGroup: PlacedGroup;

  /**
   * Group definition containing tools
   */
  group: Group;

  /**
   * Pixels per second for positioning and sizing
   */
  pixelsPerSecond: number;

  /**
   * Whether this group block is currently selected (test override)
   */
  isSelected?: boolean;
}

export const GroupBlock = memo(function GroupBlock({
  placedGroup,
  group,
  pixelsPerSecond,
  isSelected: isSelectedProp,
}: GroupBlockProps) {
  const groupBlockRef = useRef<HTMLDivElement>(null);

  const { isSelected, handleSelectionClick } = useTimelineBlock({
    id: placedGroup.id,
    isSelectedOverride: isSelectedProp,
  });

  // Hover state for delete button visibility
  const [isHovered, setIsHovered] = useState(false);
  const enterGroupEditMode = useUiStore((state) => state.enterGroupEditMode);

  // Multi-puppet support: check if this group belongs to the active puppet
  const activePuppetId = usePuppetStore((state) => state.activePuppetId);
  const getPuppetNumber = usePuppetStore((state) => state.getPuppetNumber);
  const puppets = usePuppetStore((state) => state.puppets);
  const isFromInactivePuppet =
    placedGroup.puppetId != null && placedGroup.puppetId !== activePuppetId;

  // Calculate dimensions
  const groupDuration = useMemo(() => getGroupDuration(group), [group]);
  const actualWidth = groupDuration * pixelsPerSecond;
  const visualWidth = Math.max(MIN_VISUAL_WIDTH, actualWidth);
  const leftPosition = placedGroup.startTime * pixelsPerSecond;

  // Set up drag for repositioning
  const [{ isDragging }, drag] = useDrag<
    PlacedGroupDragItem,
    void,
    { isDragging: boolean }
  >(
    () => ({
      type: DND_ITEM_TYPES.PLACED_GROUP,
      item: (monitor) => {
        // Calculate grab offset based on where user clicked within the group
        const groupElement = groupBlockRef.current;
        const clientOffset = monitor.getClientOffset();
        let grabOffsetX = 0;

        if (groupElement && clientOffset) {
          const rect = groupElement.getBoundingClientRect();
          grabOffsetX = clientOffset.x - rect.left;
        }

        return {
          type: DND_ITEM_TYPES.PLACED_GROUP,
          placedGroupId: placedGroup.id,
          originalTrackNumber: placedGroup.trackNumber,
          originalStartTime: placedGroup.startTime,
          grabOffsetX,
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [placedGroup]
  );

  // Handle double-click to enter group edit mode
  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      enterGroupEditMode(group.id);
    },
    [enterGroupEditMode, group.id]
  );

  // Handle hover delete button click
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      useTimelineStore.getState().removePlacedGroup(placedGroup.id);
      useUiStore.getState().clearSelection();
    },
    [placedGroup.id]
  );

  // Render puppet badge if multi-puppet and this group has a puppetId
  const renderPuppetBadge = () => {
    if (!placedGroup.puppetId || puppets.length <= 1) return null;
    const puppetNumber = getPuppetNumber(placedGroup.puppetId);
    if (puppetNumber === undefined) return null;

    return (
      <div
        className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center z-10"
        style={{
          backgroundColor: GROUP_COLOR,
          color: 'white',
          border: '1px solid rgba(255,255,255,0.3)',
        }}
      >
        {puppetNumber}
      </div>
    );
  };

  // Whether the delete button should be visible
  const showDeleteButton = isHovered && !isFromInactivePuppet && !isDragging;

  // Combine refs for drag and element tracking
  const setDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      groupBlockRef.current = node;
      drag(node);
    },
    [drag]
  );

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <motion.div
          ref={setDragRef}
          className="absolute top-0 h-full cursor-grab select-none"
          style={{
            left: `${leftPosition}px`,
            width: `${visualWidth}px`,
            // Dim groups from inactive puppets or while dragging
            opacity: isDragging ? 0.5 : isFromInactivePuppet ? 0.5 : 1,
            zIndex: isSelected ? 10 : 1,
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{
            opacity: isDragging ? 0.5 : isFromInactivePuppet ? 0.5 : 1,
            scale: 1,
          }}
          transition={{ duration: 0.15 }}
          onClick={handleSelectionClick}
          onDoubleClick={handleDoubleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          data-testid={`group-block-${placedGroup.id}`}
        >
          {/* Main group block container */}
          <motion.div
            className="absolute inset-0.5 rounded-md overflow-hidden"
            style={{
              backgroundColor: GROUP_COLOR,
              boxShadow: isSelected
                ? `0 0 0 2px var(--color-focus), var(--shadow-md)`
                : 'var(--shadow-sm)',
              border: isSelected
                ? '2px solid var(--color-focus)'
                : '1px solid rgba(255, 255, 255, 0.1)',
            }}
            whileHover={{
              backgroundColor: GROUP_HOVER_COLOR,
              boxShadow: isSelected
                ? `0 0 0 2px var(--color-focus), var(--shadow-md)`
                : 'var(--shadow-md)',
            }}
            transition={{ duration: 0.15 }}
          >
            {/* Group name label */}
            <div className="absolute inset-0 flex items-center justify-center px-2 overflow-hidden">
              <span
                className="text-xs font-medium text-white truncate"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
              >
                {group.name}
              </span>
            </div>

            {/* Group icon indicator (folder/stack icon) */}
            <div className="absolute top-1 left-1 opacity-60">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white"
              >
                <rect x="2" y="6" width="20" height="14" rx="2" />
                <path d="M2 10h20" />
              </svg>
            </div>

            {/* Tool count badge */}
            <div
              className="absolute bottom-1 right-1 px-1 rounded text-[10px] font-medium"
              style={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                color: 'white',
              }}
            >
              {group.tools.length}
            </div>

            {/* Hover delete button */}
            {showDeleteButton && (
              <button
                className="absolute top-0.5 right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full"
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                }}
                onClick={handleDelete}
                aria-label="Delete placed group"
                data-testid={`group-block-delete-${placedGroup.id}`}
              >
                <X className="h-2.5 w-2.5 text-white" />
              </button>
            )}
          </motion.div>

          {/* Puppet badge */}
          {renderPuppetBadge()}
        </motion.div>
      </Tooltip.Trigger>

      <Tooltip.Portal>
        <Tooltip.Content
          className="rounded-md px-3 py-2 text-sm shadow-lg"
          style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            color: 'white',
          }}
          sideOffset={5}
        >
          <div className="flex flex-col gap-1">
            <span className="font-medium">{group.name}</span>
            <span className="text-gray-400">
              {group.tools.length} tool{group.tools.length !== 1 ? 's' : ''} •{' '}
              {formatDurationDisplay(groupDuration)}
            </span>
            <span className="text-gray-500 text-xs">Double-click to edit</span>
          </div>
          <Tooltip.Arrow className="fill-[#1a1a1a]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
});
